import React, { useState, useEffect, useRef } from 'react';
import { PortfolioData } from '../data/PortfolioData';

// Auto-resizing textarea component
const AutoTextArea = ({ value, onChange, className }) => {
  const textAreaRef = useRef(null);

  useEffect(() => {
    if (textAreaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textAreaRef.current.style.height = 'auto';
      // Set height to scrollHeight to fit content
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textAreaRef}
      value={value}
      onChange={onChange}
      className={`${className} overflow-hidden resize-none`}
      rows={1}
    />
  );
};

const PortfolioVisualEditor = ({ onSave }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    // Deep clone to avoid mutating the original imported object directly
    setData(JSON.parse(JSON.stringify(PortfolioData)));
  }, []);

  if (!data) return <div>Loading...</div>;

  const handleChange = (section, field, value) => {
    setData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleArrayChange = (section, index, field, value) => {
    setData(prev => {
      const newArray = [...prev[section]];
      newArray[index] = { ...newArray[index], [field]: value };
      return { ...prev, [section]: newArray };
    });
  };

  const handleStringArrayChange = (section, index, value) => {
    setData(prev => {
      const newArray = [...prev[section]];
      newArray[index] = value;
      return { ...prev, [section]: newArray };
    });
  };

  const handleNestedStringArrayChange = (section, subSection, index, value) => {
    setData(prev => {
      const newSubArray = [...prev[section][subSection]];
      newSubArray[index] = value;
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [subSection]: newSubArray
        }
      };
    });
  };

  const handleNestedArrayChange = (section, subSection, index, field, value) => {
    setData(prev => {
      const newSubArray = [...prev[section][subSection]];
      newSubArray[index] = { ...newSubArray[index], [field]: value };
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [subSection]: newSubArray
        }
      };
    });
  };

  const handleSave = () => {
    onSave(JSON.stringify(data, null, 2));
  };

  return (
    <div className="space-y-8 text-left">
      <div className="flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 p-4 z-10 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Visual Editor: PortfolioData</h2>
        <button 
          onClick={handleSave}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors"
        >
          Apply Changes to JSON
        </button>
      </div>

      {/* Narrative Section */}
      <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Narrative</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Elevator Pitch</label>
            <AutoTextArea 
              className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              value={data.narrative.elevatorPitch}
              onChange={(e) => handleChange('narrative', 'elevatorPitch', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Background</label>
            <AutoTextArea 
              className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              value={data.narrative.background}
              onChange={(e) => handleChange('narrative', 'background', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Why IT?</label>
            <AutoTextArea 
              className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              value={data.narrative.whyIT}
              onChange={(e) => handleChange('narrative', 'whyIT', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Experience Section */}
      <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Experience</h3>
        <div className="space-y-8">
          {data.experience.map((exp, index) => (
            <div key={index} className="p-4 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Job Title</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                    value={exp.jobTitle}
                    onChange={(e) => handleArrayChange('experience', index, 'jobTitle', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Company</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                    value={exp.company}
                    onChange={(e) => handleArrayChange('experience', index, 'company', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                    value={exp.startDate}
                    onChange={(e) => handleArrayChange('experience', index, 'startDate', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">End Date</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                    value={exp.endDate}
                    onChange={(e) => handleArrayChange('experience', index, 'endDate', e.target.value)}
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Context</label>
                <AutoTextArea 
                  className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                  value={exp.context}
                  onChange={(e) => handleArrayChange('experience', index, 'context', e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Career Goals Section */}
      <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Career Goals</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Salary Expectation</label>
            <input 
              type="text"
              className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              value={data.careerGoals.salaryExpectation}
              onChange={(e) => handleChange('careerGoals', 'salaryExpectation', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
            <input 
              type="text"
              className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              value={data.careerGoals.location}
              onChange={(e) => handleChange('careerGoals', 'location', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Availability</label>
            <input 
              type="text"
              className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              value={data.careerGoals.availability}
              onChange={(e) => handleChange('careerGoals', 'availability', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Recruiter Q&A Section */}
      <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Recruiter Q&A</h3>
        <div className="space-y-4">
          {data.recruiterQA.map((qa, index) => (
            <div key={index} className="p-4 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Question</label>
                <input 
                  type="text" 
                  className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white font-medium" 
                  value={qa.question}
                  onChange={(e) => handleArrayChange('recruiterQA', index, 'question', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Answer</label>
                <AutoTextArea 
                  className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                  value={qa.answer}
                  onChange={(e) => handleArrayChange('recruiterQA', index, 'answer', e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortfolioVisualEditor;